/*
 * previewHighlight.ts — text-walker helpers for the global preview search.
 *
 * Extracted from the original PreviewSearch.svelte overlay (Phase 15) when
 * the search moved to the header. Same load-bearing constraints carry over:
 *
 *   - Walks ONLY text nodes (TreeWalker SHOW_TEXT) so we never touch element
 *     structure. Mutating during walk would invalidate the iterator —
 *     snapshot first, mutate second.
 *
 *   - Skips pre / code / .katex / svg / mark[data-search]. Code blocks may
 *     already be Shiki-tokenised into nested spans, and re-wrapping their
 *     text destroys the highlighting structure. KaTeX + SVG mix structure
 *     with text — splitting text nodes inside them breaks layout. The
 *     already-highlighted skip prevents re-entry corruption when the
 *     effect re-runs on every keystroke.
 *
 *   - `re.lastIndex = 0` after `test()` is load-bearing — /g regexes
 *     preserve lastIndex across calls, so the subsequent `replace()` would
 *     skip the first portion of each text node on a hit otherwise.
 *
 *   - We never cache innerHTML. Each pass unwraps any existing marks then
 *     re-wraps current matches against the LIVE DOM. If the preview
 *     re-rendered under us, our overlay annotations are simply gone — no
 *     stale snapshot can clobber the new content (P1.1 fix from the
 *     2026-05-23 codex audit).
 */

export function escapeRegex(q: string): string {
  return q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export function unwrapSearchMarks(target: HTMLElement): void {
  const marks = Array.from(target.querySelectorAll('mark[data-search]'))
  for (const m of marks) {
    const text = document.createTextNode(m.textContent ?? '')
    m.replaceWith(text)
  }
  // Coalesce adjacent text nodes so a subsequent walk sees "foo" + "bar"
  // as "foobar" and matches across the seam.
  if (marks.length > 0) target.normalize()
}

export function highlightMatches(target: HTMLElement, re: RegExp): HTMLElement[] {
  const walker = document.createTreeWalker(target, NodeFilter.SHOW_TEXT)
  const nodes: Text[] = []
  let n: Node | null
  while ((n = walker.nextNode())) nodes.push(n as Text)
  for (const node of nodes) {
    const parent = node.parentElement
    if (!parent || parent.closest('pre, code, .katex, svg, mark[data-search]')) continue
    const txt = node.nodeValue ?? ''
    if (!re.test(txt)) continue
    re.lastIndex = 0
    // Build real DOM nodes (not innerHTML string assembly) so user-text is
    // immune to HTML reinterpretation without an escape pass.
    const frag = document.createDocumentFragment()
    let lastIndex = 0
    txt.replace(re, (m, _g1, offset: number) => {
      if (offset > lastIndex) {
        frag.appendChild(document.createTextNode(txt.slice(lastIndex, offset)))
      }
      const mark = document.createElement('mark')
      mark.setAttribute('data-search', '')
      mark.textContent = m
      frag.appendChild(mark)
      lastIndex = offset + m.length
      return m
    })
    if (lastIndex < txt.length) {
      frag.appendChild(document.createTextNode(txt.slice(lastIndex)))
    }
    parent.replaceChild(frag, node)
  }
  return Array.from(target.querySelectorAll('mark[data-search]')) as HTMLElement[]
}
